import { Image as AppImage } from "../model/image";

const MAX_SIZE = 1920;
const JPEG_QUALITY = 0.8;

export type FileData = {
  file: File;
  dataUrl: string;
}

// Main function to compress the image
export async function getCompressedImageFile(file: File): Promise<FileData> {
  const img = new Image();
  img.src = await readFileAsDataURL(file); // Convert file to Data URL
  await new Promise(resolve => (img.onload = resolve));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Get scaled dimensions
  const { width, height } = getScaledDimensions(img.width, img.height, MAX_SIZE);
  canvas.width = width;
  canvas.height = height;

  // Draw the resized image on the canvas
  ctx.drawImage(img, 0, 0, width, height);

  // Convert the canvas to a compressed Data URL
  const compressedDataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const newFileName = changeFileExtension(file.name, 'jpg');

  // Convert the Data URL back to a File
  return {
    file: dataUrlToFile(compressedDataUrl, newFileName, file.type),
    dataUrl: compressedDataUrl
  };
}

export async function getCompressedImage(image: AppImage): Promise<AppImage> {
  if (!image.source) throw new Error("Image has no source File");

  const compressedFileData = await getCompressedImageFile(image.source);

  return new AppImage(compressedFileData.dataUrl, compressedFileData.file);
}

// Helper to convert File to Data URL
function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = err => reject(err);
    reader.readAsDataURL(file);
  });
}

// Helper to scale dimensions while maintaining the aspect ratio
function getScaledDimensions(originalWidth: number, originalHeight: number, maxSize: number): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;

  if (originalWidth > maxSize || originalHeight > maxSize) {
    if (originalWidth > originalHeight) {
      return { width: maxSize, height: Math.round(maxSize / aspectRatio) };
    } else {
      return { width: Math.round(maxSize * aspectRatio), height: maxSize };
    }
  }

  return { width: originalWidth, height: originalHeight };
}

// Helper to convert Data URL back to a File
function dataUrlToFile(dataUrl: string, fileName: string, mimeType: string): File {
  const byteString = atob(dataUrl.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], fileName, { type: mimeType });
}

// Helper to change the file extension
function changeFileExtension(fileName: string, newExtension: string): string {
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, ''); // Remove the current extension
  return `${nameWithoutExtension}.${newExtension}`; // Add the new extension
}
